// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  User,
  getOrganizationUsers,
} from '@/account/api/get-organization-users';
import {
  UserRole,
  UserStatus,
  userRoleLabelMap,
  userStatusLabelMap,
} from '@/account/api/types';
import {
  activateUser,
  deactivateUser,
  demoteUserFromAdmin,
  promoteUserToAdmin,
} from '@/account/api/update-user-status';
import { EditOrganizationUserDialog } from '@/account/components/edit-organization-user-dialog';
import { UserRoleFilterMenu } from '@/account/components/user-role-filter-menu';
import { UserStatusFilterMenu } from '@/account/components/user-status-filter-menu';
import { handleAPIError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { TableControlContainer } from '@/components/table-control-container';
import { MoreVert as MoreVertIcon } from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import {
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useState } from 'react';

interface Props {
  /**
   * The key of the organization whose users should be displayed
   */
  organizationKey: string;
}

/**
 * A simple label component that displays "Admin Only" in a muted style
 */
function AdminLabel() {
  return (
    <Typography
      component="span"
      variant="body2"
      color="text.secondary"
      display="inline"
      sx={{
        textTransform: 'lowercase',
        fontVariant: 'small-caps',
        marginLeft: 1,
      }}
    >
      Admin only
    </Typography>
  );
}

/**
 * Displays a table of users in an organization, showing their names, email addresses,
 * admin status, and account status.
 * The list is sorted alphabetically by name.
 */
export function OrganizationUserList(props: Props) {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const { user: currentUser } = useCurrentUser();
  const toast = useToast();
  const { data } = useSuspenseQuery({
    queryKey: ['organization-users', props.organizationKey],
    queryFn: () => getOrganizationUsers(props.organizationKey),
  });

  // State for filters
  const [statusFilter, setStatusFilter] = useState<UserStatus[]>([]);
  const [roleFilter, setRoleFilter] = useState<UserRole[]>([]);

  // State for the action menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // State for the edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  // Mutations for user status updates
  const promoteUserMutation = useMutation({
    mutationFn: (userKey: string) =>
      promoteUserToAdmin(props.organizationKey, userKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-users', props.organizationKey],
      });
      handleMenuClose();
    },
    onError: (error) => handleError(error),
  });

  const demoteUserMutation = useMutation({
    mutationFn: (userKey: string) =>
      demoteUserFromAdmin(props.organizationKey, userKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-users', props.organizationKey],
      });
      handleMenuClose();
    },
    onError: (error) => handleError(error),
  });

  const activateUserMutation = useMutation({
    mutationFn: (userKey: string) =>
      activateUser(props.organizationKey, userKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-users', props.organizationKey],
      });
      handleMenuClose();
    },
    onError: (error) => handleError(error),
  });

  const deactivateUserMutation = useMutation({
    mutationFn: (userKey: string) =>
      deactivateUser(props.organizationKey, userKey),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['organization-users', props.organizationKey],
      });
      handleMenuClose();
    },
    onError: (error) => handleError(error),
  });

  /**
   * Returns styling for user table rows based on user status
   * @param user The user whose data is being displayed
   * @returns The sx prop object with appropriate styling
   */
  const getUserRowSx = (user: User) => ({
    backgroundColor:
      user.id === currentUser.id
        ? alpha(theme.palette.text.secondary, 0.1)
        : user.isDeactivated
          ? alpha(theme.palette.error.light, 0.1)
          : 'inherit',
    opacity: user.isDeactivated ? 0.8 : 1,
  });

  /**
   * Returns styling for user table cells based on user status
   * @param user The user whose data is being displayed
   * @returns The sx prop object with appropriate styling
   */
  const getUserCellSx = (user: User) => ({
    fontWeight: user.id === currentUser.id ? 'bold' : 'inherit',
    color: user.isDeactivated ? 'error.light' : 'inherit',
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleError = (error: AxiosError) => {
    if (error.response?.status === 403) {
      toast.showToast({
        severity: 'error',
        element: (
          <span>You don't have permission to manage organization users</span>
        ),
        duration: 5000,
      });
    } else {
      const apiError = handleAPIError(error);
      const cannotModifySelf = apiError.errorType === 'user_cannot_modify_self';

      if (cannotModifySelf) {
        toast.showToast({
          severity: 'warning',
          element: <span>You cannot modify your own user account</span>,
          duration: 5000,
        });
      } else {
        toast.showToast({
          severity: 'error',
          element: (
            <span>An error occurred while updating the user status</span>
          ),
          duration: 5000,
        });
      }
    }
  };

  const handleStatusUpdate = (
    action: 'promote' | 'demote' | 'activate' | 'deactivate'
  ) => {
    if (!selectedUser) return;

    const userKey = selectedUser.key; // Capture the key before any state changes

    switch (action) {
      case 'promote':
        promoteUserMutation.mutate(userKey);
        break;
      case 'demote':
        demoteUserMutation.mutate(userKey);
        break;
      case 'activate':
        activateUserMutation.mutate(userKey);
        break;
      case 'deactivate':
        deactivateUserMutation.mutate(userKey);
        break;
    }
  };

  const handleEdit = () => {
    if (selectedUser) {
      setEditingUser(selectedUser);
      setEditDialogOpen(true);
    }
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
  };

  /**
   * Filters users based on the selected status and role filters
   * @returns Filtered array of users
   */
  const getFilteredUsers = () => {
    return data.users.filter((user) => {
      // If no filters are selected, show all users
      if (statusFilter.length === 0 && roleFilter.length === 0) {
        return true;
      }

      // Check status filter
      if (statusFilter.length > 0) {
        const userStatus: UserStatus = user.isDeactivated
          ? 'deactivated'
          : 'active';

        // If the user status is not in the status filter, return false (exclude the user)
        if (!statusFilter.includes(userStatus)) {
          return false;
        }
      }

      // Check role filter
      if (roleFilter.length > 0) {
        const userRole: UserRole = user.isAdmin ? 'admin' : 'user';

        // If the user role is not in the role filter, return false (exclude the user)
        if (!roleFilter.includes(userRole)) {
          return false;
        }
      }

      // By default, show all users
      return true;
    });
  };

  if (data.users.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No users found in this organization
      </Typography>
    );
  }

  return (
    <Stack>
      <TableControlContainer paddingTop={0}>
        <Stack direction="row" spacing={1}>
          <UserStatusFilterMenu
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <UserRoleFilterMenu value={roleFilter} onChange={setRoleFilter} />
        </Stack>
      </TableControlContainer>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>First name</TableCell>
              <TableCell>Last name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Role</TableCell>
              {currentUser.isAdmin && <TableCell width={50} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredUsers().map((user) => (
              <TableRow key={user.id} sx={getUserRowSx(user)}>
                <TableCell sx={getUserCellSx(user)}>
                  {user.firstName ?? (
                    <Typography variant="body2" color="text.secondary">
                      None
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={getUserCellSx(user)}>
                  {user.lastName ?? (
                    <Typography variant="body2" color="text.secondary">
                      None
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={getUserCellSx(user)}>{user.email}</TableCell>
                <TableCell>
                  <Tooltip
                    title={`Last updated ${user.lastDeactivationChangeAt.toLocaleString(DateTime.DATETIME_SHORT)}`}
                    arrow
                  >
                    <Chip
                      size="small"
                      color={user.isDeactivated ? 'error' : 'success'}
                      label={
                        userStatusLabelMap[
                          user.isDeactivated ? 'deactivated' : 'active'
                        ]
                      }
                      variant="outlined"
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip
                    title={`Last updated ${user.lastAdminStatusChangeAt.toLocaleString(DateTime.DATETIME_SHORT)}`}
                    arrow
                  >
                    <Chip
                      size="small"
                      color="primary"
                      label={userRoleLabelMap[user.isAdmin ? 'admin' : 'user']}
                      variant={user.isAdmin ? 'filled' : 'outlined'}
                    />
                  </Tooltip>
                </TableCell>
                {currentUser.isAdmin && (
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, user)}
                      aria-label="user actions"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleEdit}>
            Edit user details
            <AdminLabel />
          </MenuItem>
          {!selectedUser?.isDeactivated && (
            <MenuItem onClick={() => handleStatusUpdate('deactivate')}>
              Deactivate account
              <AdminLabel />
            </MenuItem>
          )}
          {selectedUser?.isDeactivated && (
            <MenuItem onClick={() => handleStatusUpdate('activate')}>
              Activate account
              <AdminLabel />
            </MenuItem>
          )}
          {selectedUser && !selectedUser.isAdmin && (
            <MenuItem
              onClick={() => handleStatusUpdate('promote')}
              disabled={selectedUser.isDeactivated}
            >
              Make admin
              <AdminLabel />
            </MenuItem>
          )}
          {selectedUser?.isAdmin && (
            <MenuItem
              onClick={() => handleStatusUpdate('demote')}
              disabled={selectedUser.isDeactivated}
            >
              Remove admin
              <AdminLabel />
            </MenuItem>
          )}
        </Menu>
      </TableContainer>

      <EditOrganizationUserDialog
        key={editingUser?.key}
        isOpen={editDialogOpen}
        onClose={handleEditClose}
        user={editingUser}
        organizationKey={props.organizationKey}
      />
    </Stack>
  );
}
